import { useCallback, useRef, useState } from "react";
import { RejectionReason } from "../../redux/onBoardingSlice.types";
import { fetchDocumentSasUrl } from "../../redux/onBoardingSlice";
import { DocFieldState } from "../types";
import { resolveReason } from "../utils";
import { DOC_STATE } from "./constants";

/**
 * Manages document viewer state (collapsed / preview / rejecting / rejected),
 * SAS URL loading, and all doc-level event handlers.
 *
 * Uses a ref mirror of `docStates` to avoid stale closures in the async
 * `onView` handler without adding `docStates` to dependency arrays.
 *
 * Isolated from rendering so it can be tested with `renderHook`.
 */
export const useDocStates = (dispatch: (action: any) => any) => {
  const [docStates, setDocStates] = useState<Record<string, DocFieldState>>({});
  const [sasUrls, setSasUrls] = useState<Record<string, string>>({});
  const [docLoadingMap, setDocLoadingMap] = useState<Record<string, boolean>>({});

  // Always up-to-date ref so async onView reads current state, not a stale closure.
  const docStatesRef = useRef(docStates);
  docStatesRef.current = docStates;

  const initDocStates = useCallback((fieldIds: string[]) => {
    const initial: Record<string, DocFieldState> = {};
    fieldIds.forEach((id) => {
      initial[id] = {
        state: DOC_STATE.COLLAPSED,
        viewed: false,
        reasons: [],
        pendingReason: "",
        pendingComment: "",
      };
    });
    setDocStates(initial);
    setSasUrls({});
    setDocLoadingMap({});
  }, []);

  /**
   * Returns a stable set of handlers for a single document field.
   * The factory itself is memoised; individual handler objects are lightweight
   * closures created per-call (one per field per render).
   */
  const makeDocHandlers = useCallback(
    (fieldId: string, imageUrl: string, reasons: RejectionReason[] = []) => ({
      onView: async () => {
        const currentState = docStatesRef.current[fieldId]?.state;
        if (currentState === DOC_STATE.PREVIEW || currentState === DOC_STATE.REJECTING) {
          setDocStates((prev) => ({
            ...prev,
            [fieldId]: { ...prev[fieldId], state: DOC_STATE.COLLAPSED },
          }));
          return;
        }
        setDocLoadingMap((prev) => ({ ...prev, [fieldId]: true }));
        const action = await dispatch(fetchDocumentSasUrl({ imageUrl }));
        let sasUrl: string | undefined;
        if (fetchDocumentSasUrl.fulfilled.match(action)) {
          const result = action.payload;
          sasUrl = result?.results || result?.url || result?.sasUrl;
        }
        setDocLoadingMap((prev) => ({ ...prev, [fieldId]: false }));
        if (!sasUrl) return;
        const resolvedUrl: string = sasUrl;
        setSasUrls((prev) => ({ ...prev, [fieldId]: resolvedUrl }));
        setDocStates((prev) => ({
          ...prev,
          [fieldId]: {
            ...prev[fieldId],
            state: DOC_STATE.PREVIEW,
            viewed: true,
            pendingReason: "",
            pendingComment: "",
          },
        }));
      },

      onStartRejection: () =>
        setDocStates((prev) => ({
          ...prev,
          [fieldId]: { ...prev[fieldId], state: DOC_STATE.REJECTING },
        })),

      onSelectReason: (reason: string) =>
        setDocStates((prev) => ({
          ...prev,
          [fieldId]: { ...prev[fieldId], pendingReason: reason, pendingComment: "" },
        })),

      onCommentChange: (value: string) =>
        setDocStates((prev) => ({
          ...prev,
          [fieldId]: { ...prev[fieldId], pendingComment: value },
        })),

      onClearAll: () =>
        setDocStates((prev) => ({
          ...prev,
          [fieldId]: { ...prev[fieldId], pendingReason: "", pendingComment: "" },
        })),

      onCancelRejection: () =>
        setDocStates((prev) => ({
          ...prev,
          [fieldId]: { ...prev[fieldId], state: DOC_STATE.PREVIEW, pendingReason: "", pendingComment: "" },
        })),

      onConfirmRejection: () =>
        setDocStates((prev) => {
          const d = prev[fieldId];
          const reason = resolveReason(d?.pendingReason ?? "", d?.pendingComment ?? "", reasons);
          return {
            ...prev,
            [fieldId]: {
              ...d,
              state: DOC_STATE.REJECTED,
              reasons: reason ? [reason] : [],
              pendingReason: "",
              pendingComment: "",
            },
          };
        }),
    }),
    [dispatch]
  );

  return { docStates, sasUrls, docLoadingMap, initDocStates, makeDocHandlers };
};
